import { useEffect, useMemo } from "react";
import { useNavigate } from "react-router";
import _ from "lodash";

import { URLS } from "config/urls";
import { colors } from "theme/palette";

import Box from "components/Box";
import ListPagination from "components/ListPagination";
import Skeleton from "components/Skeleton";
import Stack from "components/Stack";
import Text from "components/Text";

import { useDealTablesWithTableImport } from "entities/Deal/sdk";
import TableImportCreateDialogButton from "entities/TableStorage/components/TableImportCreateDialogButton";
import { useDealTableUpdateSucceeded } from "entities/TableStorage/hooks";

import Details from "./components/Details";
import ExplanatoryBox from "./components/ExplanatoryBox";

const ProformaOtherData = ({ dealId }: { dealId: number }) => {
  const {
    data: tables,
    error: tableError,
    mutate: mutateTables,
    pagesCount,
    setPage,
    currentPage,
    isLoading,
  } = useDealTablesWithTableImport({
    dealId,
    limit: 10,
  });
  const navigate = useNavigate();

  useEffect(() => {
    if (tableError?.status === 404) {
      navigate(URLS.NOT_FOUND, { replace: true });
    }

    if (
      tableError?.status === 400 &&
      tableError?.message === "Company does not have access to this feature."
    ) {
      navigate(URLS.FEATURE_ACCESS_DENIED, { replace: true });
    }
  }, [tableError, navigate]);

  useDealTableUpdateSucceeded({
    successEventHandler: () => {
      mutateTables();
    },
  });

  const showPagination = useMemo(
    () => !isLoading && !_.isUndefined(tables) && tables.count > 0,
    [isLoading, tables]
  );

  useEffect(() => {
    if (!_.isUndefined(currentPage)) {
      window.scrollTo({ top: 0, left: 0, behavior: "smooth" });
    }
  }, [currentPage]);

  return (
    <>
      <Text variant="text3" color={colors.gray80} fontWeight={500}>
        {_.isUndefined(tables) ? (
          <Skeleton width={10} sx={{ display: "inline-block" }} />
        ) : (
          tables.count
        )}{" "}
        data table
        {tables?.count !== 1 ? "s" : ""} created
      </Text>
      {!_.isNil(tables) ? (
        tables.results.map((table) => (
          <Details
            key={table.id}
            dealId={dealId}
            table={table}
            mutateTables={mutateTables}
          />
        ))
      ) : (
        <Stack spacing={5}>
          <Skeleton variant="rounded" sx={{ height: "100px", width: "100%" }} />
          <Skeleton variant="rounded" sx={{ height: "350px", width: "100%" }} />
          <Skeleton variant="rounded" sx={{ height: "100px", width: "100%" }} />

          <Skeleton variant="rounded" sx={{ height: "350px", width: "100%" }} />
        </Stack>
      )}
      <TableImportCreateDialogButton dealId={dealId} />
      {showPagination && (
        <Box marginTop={3}>
          <ListPagination
            setPage={setPage}
            pagesCount={pagesCount}
            currentPage={currentPage}
          />
        </Box>
      )}
      <ExplanatoryBox />
    </>
  );
};

export default ProformaOtherData;
